import assetUtils, { Asset } from 'app-lib/assetUtils'
import providerUtils from 'app-lib/providerUtils'
import { approvalUtils } from 'app-lib/web3'
import { bscCompensationContract, chainConfigs } from 'bzx-common/src/config/appConfig'
import { Airdrop, IAirdropInfo } from 'bzx-common/src/services/AirdropService'
import { AirdropStore, RootStore } from 'bzx-common/src/stores'
import * as mobx from 'mobx'
import { FormEvent } from 'react'
import { DialogVM } from 'ui-framework'
import web3Utils from 'web3-utils'
import { BigNumber } from '@0x/utils'

export type CompensationTab = 'bzrxOnPolygon' | 'bzrxOnBsc' | 'p125' | ''
export type BscCompensationTab = 'bsc' | 'polygon' | ''

// Note keep these lowercase
const hackerWalletsRug2 = [
  '0x53cb834c43ce919289a4db2ca54f65e1afb17041',
  '0xafad9352eb6bcd085dd68268d353d0ed2571af89',
]

interface ICompensationVMProps {
  inputAddress: string
  approvalsPending: boolean
  approvalsError: Error | null
  approvalsChecked: boolean
  bzrxSentToBscCompensationContract: boolean
  tab: CompensationTab
  bscTab: BscCompensationTab
  approvals: {
    amount: BigNumber
    spender: string
    assetAddress: string
  }[]
}

export default class CompensationVM implements ICompensationVMProps {
  rootStore: RootStore
  airdropStore: AirdropStore
  inputAddress = ''
  approvalsPending = false
  approvalsError: Error | null = null
  approvalsChecked = false
  bzrxSentToBscCompensationContract = false
  instructionsDialog = new DialogVM({ id: 'instructions' })
  priceRefDialog = new DialogVM({ id: 'priceref' })
  tab: CompensationTab = ''
  bscTab: BscCompensationTab = ''
  approvals: {
    amount: BigNumber
    spender: string
    assetAddress: string
  }[] = []
  stopSyncBalance?: mobx.IReactionDisposer

  get canCheck() {
    return web3Utils.isAddress(this.inputAddress)
  }

  get canCheckP125() {
    const { userClaimed } = this.airdropStore.airdropsInfo.P125_COMPENSATION1
    return web3Utils.isAddress(this.inputAddress) && !userClaimed
  }

  get approvalsAreSafe() {
    return (
      this.approvalsChecked &&
      !this.approvalsError &&
      this.approvals.length > 0 &&
      this.approvals.every((approval) => approval.amount.isZero())
    )
  }

  get inputAddressEqualConnected() {
    const { walletAddress } = this.rootStore.web3Connection
    return walletAddress.toLowerCase() === this.inputAddress.toLowerCase()
  }

  /** This is for the BZRX / GOV that was stolen */
  get canClaimBZRX() {
    const { chainId } = this.rootStore.web3Connection
    if (
      chainId !== 137 ||
      this.airdropStore.airdropPending ||
      !this.inputAddressEqualConnected ||
      !this.approvalsAreSafe
    ) {
      return false
    }
    const { BZRX_COMPENSATION1, BZRX_COMPENSATION2 } = this.airdropStore.airdropsInfo
    return [BZRX_COMPENSATION1, BZRX_COMPENSATION2].some((compensation) => {
      const { amount, userClaimed } = compensation
      return amount.gt(0) && !userClaimed
    })
  }

  get canClaimP125() {
    const { chainId } = this.rootStore.web3Connection
    const { amount, userClaimed } = this.airdropStore.airdropsInfo.P125_COMPENSATION1
    return (
      amount.gt(0) &&
      !userClaimed &&
      this.inputAddressEqualConnected &&
      !this.airdropStore.airdropPending &&
      chainId === 137
    )
  }

  /**
   * Show different data for hacker
   */
  get isBadBoy() {
    const { walletAddress } = this.rootStore.web3Connection
    return (
      hackerWalletsRug2.includes(walletAddress.toLowerCase()) ||
      hackerWalletsRug2.includes(this.inputAddress.toLowerCase())
    )
  }

  get canCheckApprovals() {
    return web3Utils.isAddress(this.inputAddress) && !this.approvalsChecked
  }

  addP125Token() {
    const { chainId } = this.rootStore.web3Connection
    if (chainId === 137) {
      providerUtils.addToken(chainId, Asset.P125)
    }
  }

  set(prop: keyof ICompensationVMProps, value: any) {
    ;(this[prop] as any) = value
  }

  checkBscBalance() {
    const { userWallet } = this.rootStore
    this.assign({ bzrxSentToBscCompensationContract: false })
    userWallet.getBalances([Asset.BZRX])
  }

  changeTab(tab: CompensationTab) {
    const { web3Connection } = this.rootStore
    this.tab = tab
    this.reset()
    if (this.tab === 'bzrxOnBsc' && web3Connection.chainId === 56 && web3Connection.walletAddress) {
      this.checkBscBalance()
    }
  }

  changeBscTab(tab: BscCompensationTab) {
    this.bscTab = tab
    this.reset()
  }

  hasCompensations(compensations: IAirdropInfo[]) {
    return compensations.find((compensation) => compensation.amount.gt(0))
  }

  assign(props: Partial<ICompensationVMProps>) {
    Object.assign(this, props)
  }

  reset(address = '') {
    this.approvals = []
    this.bzrxSentToBscCompensationContract = false
    this.approvalsError = null
    this.approvalsPending = false
    this.approvalsChecked = false
    this.airdropStore.clearData()
    this.inputAddress = address || this.rootStore.web3Connection.walletAddress
  }

  async checkAirdrop(event: FormEvent) {
    event.preventDefault()
    this.reset(this.inputAddress)
    switch (this.tab) {
      case 'bzrxOnBsc': {
        await this.airdropStore.checkBzrxLockedAirdrops(this.inputAddress)
        break
      }
      case 'bzrxOnPolygon': {
        await this.airdropStore.getUserAirdropInfo(Airdrop.BZRX_COMPENSATION1, this.inputAddress)
        this.airdropStore.getUserAirdropInfo(Airdrop.BZRX_COMPENSATION2, this.inputAddress)
        break
      }
      case 'p125': {
        this.airdropStore.getUserAirdropInfo(Airdrop.P125_COMPENSATION1, this.inputAddress)
        break
      }
    }
  }

  async checkDangerousApprovals() {
    this.assign({ approvalsPending: true, approvalsChecked: false })
    try {
      const BZRXAddress = assetUtils.getAddress(137, Asset.BZRX)
      const { compromisedContracts } = chainConfigs[137]
      const approvals = await approvalUtils.getSpendingApprovalMultipleSpenders(
        this.rootStore.fulcrumProvider,
        this.inputAddress,
        BZRXAddress,
        compromisedContracts
      )
      this.set('approvals', approvals)
    } catch (err) {
      this.assign({ approvalsError: err })
      console.error(err)
    } finally {
      this.assign({ approvalsPending: false, approvalsChecked: true })
    }
  }

  async sendBZRXtoBscCompensationContract() {
    const { userWallet } = this.rootStore
    const balance = userWallet.findTokenBalance(Asset.BZRX)
    if (balance.gt(0)) {
      const tx = await userWallet.sendToken(Asset.BZRX, bscCompensationContract, balance)
      if (tx && tx.success) {
        this.assign({ bzrxSentToBscCompensationContract: true })
      }
    }
  }

  init() {
    const { web3Connection } = this.rootStore
    this.inputAddress = web3Connection.walletAddress
    this.stopSyncBalance = mobx.autorun(() => {
      this.reset(web3Connection.walletAddress)
      if (web3Connection.walletAddress && web3Connection.chainId === 56) {
        this.checkBscBalance()
      }
    })
  }

  destroyVM() {
    this.stopSyncBalance && this.stopSyncBalance()
  }

  constructor({ rootStore }: { rootStore: RootStore }) {
    this.rootStore = rootStore
    this.airdropStore = rootStore.airdropStore
    mobx.makeAutoObservable(this, undefined, { autoBind: true, deep: false })
    this.init()
  }
}
